import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import { enqueueSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import {
  confirmExperienceOrderAdmin,
  updateExperienceOrderStatus,
} from '../../actions/experienceAuth';

const LIFECYCLE_STEPS = [
  { key: 'PENDING_FOR_APPROVAL', label: 'Pending Approval' },
  { key: 'VERIFY', label: 'Under Verification' },
  { key: 'NEED_TO_PAYMENT', label: 'Ready for Payment' },
  { key: 'CONFIRM', label: 'Confirmed & Paid' },
];

const ALL_STATUS_OPTIONS = [
  { value: 'PENDING_FOR_APPROVAL', label: 'PENDING_FOR_APPROVAL (Initial Order)' },
  { value: 'VERIFY', label: 'VERIFY (Admin/Vendor Verification)' },
  { value: 'NEED_TO_PAYMENT', label: 'NEED_TO_PAYMENT (Approved for Payment)' },
  { value: 'CONFIRM', label: 'CONFIRM (Confirmed Booking & Paid)' },
  { value: 'COMPLETED', label: 'COMPLETED (Experience Concluded)' },
  { value: 'CANCELLED', label: 'CANCELLED (Order Cancelled)' },
];

const getStatusColor = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'PENDING_FOR_APPROVAL') return '#d97706';
  if (s === 'VERIFY') return '#2563eb';
  if (s === 'NEED_TO_PAYMENT') return '#7c3aed';
  if (s === 'CONFIRM') return '#059669';
  if (s === 'COMPLETED') return '#0d9488';
  if (s === 'CANCELLED') return '#e11d48';
  return '#64748b';
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: '#fff',
    '& fieldset': { borderColor: alpha('#11409B', 0.2) },
    '&:hover fieldset': { borderColor: '#11409B' },
  },
};

const OrderStatusModal = ({
  open,
  handleClose,
  order,
  onSuccess,
}) => {
  const dispatch = useDispatch();

  const [targetStatus, setTargetStatus] = useState('');
  const [adminRemarks, setAdminRemarks] = useState('');
  const [paymentMode, setPaymentMode] = useState('WALLET');
  const [loading, setLoading] = useState(false);

  const currentStatus = String(order?.orderStatus || order?.status || 'PENDING_FOR_APPROVAL').toUpperCase();

  // Find step index
  let activeStep = LIFECYCLE_STEPS.findIndex((s) => s.key === currentStatus);
  if (currentStatus === 'COMPLETED') activeStep = 4;
  if (currentStatus === 'CANCELLED') activeStep = -1;

  useEffect(() => {
    if (order && open) {
      // Suggest logical next status
      if (currentStatus === 'PENDING_FOR_APPROVAL') {
        setTargetStatus('VERIFY');
      } else if (currentStatus === 'VERIFY') {
        setTargetStatus('NEED_TO_PAYMENT');
      } else if (currentStatus === 'NEED_TO_PAYMENT') {
        setTargetStatus('CONFIRM');
      } else if (currentStatus === 'CONFIRM') {
        setTargetStatus('COMPLETED');
      } else {
        setTargetStatus(currentStatus);
      }
      setAdminRemarks(order.adminRemarks || '');
    }
  }, [order, open, currentStatus]);

  if (!order) return null;

  const orderId = order.id || order._id || order.orderId;

  const handleUpdateStatus = (chosenStatus = targetStatus) => {
    if (!chosenStatus) {
      enqueueSnackbar('Please select a valid target status', { variant: 'warning' });
      return;
    }

    setLoading(true);

    if (chosenStatus === 'CONFIRM') {
      // If confirming, call admin direct confirm API
      dispatch(
        confirmExperienceOrderAdmin(
          orderId,
          {
            remarks: adminRemarks.trim(),
            paymentMode,
          },
          () => {
            setLoading(false);
            enqueueSnackbar('Order confirmed and marked as PAID', { variant: 'success' });
            handleClose();
            if (onSuccess) onSuccess();
          },
          (err) => {
            // If direct confirm fails with 404 or specific code, fallback to status patch
            dispatch(
              updateExperienceOrderStatus(
                orderId,
                {
                  status: 'CONFIRM',
                  orderStatus: 'CONFIRM',
                  adminRemarks: adminRemarks.trim(),
                },
                () => {
                  setLoading(false);
                  enqueueSnackbar('Order status updated to CONFIRM', { variant: 'success' });
                  handleClose();
                  if (onSuccess) onSuccess();
                },
                (err2) => {
                  setLoading(false);
                  const msg = err2?.response?.data?.message || 'Failed to update order status';
                  enqueueSnackbar(msg, { variant: 'error' });
                }
              )
            );
          }
        )
      );
    } else {
      dispatch(
        updateExperienceOrderStatus(
          orderId,
          {
            status: chosenStatus,
            orderStatus: chosenStatus,
            adminRemarks: adminRemarks.trim(),
          },
          () => {
            setLoading(false);
            enqueueSnackbar(`Order transitioned to ${chosenStatus}`, { variant: 'success' });
            handleClose();
            if (onSuccess) onSuccess();
          },
          (err) => {
            setLoading(false);
            const msg = err?.response?.data?.message || err?.message || 'Failed to update order status';
            enqueueSnackbar(msg, { variant: 'error' });
          }
        )
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.2)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 3,
          background: 'linear-gradient(135deg, #0c3483 0%, #11409B 50%, #1e40af 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="caption" sx={{ letterSpacing: '0.08em', opacity: 0.85, fontWeight: 700 }}>
            LIFECYCLE STATE MACHINE
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mt: 0.3 }}>
            Manage Experience Order #{orderId}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: '#fff',
            bgcolor: alpha('#fff', 0.12),
            '&:hover': { bgcolor: alpha('#fff', 0.24) },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3.5, bgcolor: '#f8fafc' }}>
        {/* Stepper Header */}
        <Box
          sx={{
            p: 3,
            bgcolor: '#fff',
            borderRadius: '16px',
            border: '1px solid rgba(0,0,0,0.06)',
            mb: 3,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#64748b', mb: 2 }}>
            ORDER PROGRESSION
          </Typography>

          <Stepper activeStep={activeStep >= 0 ? activeStep : 0} alternativeLabel>
            {LIFECYCLE_STEPS.map((step, index) => {
              const isCompleted = activeStep > index || currentStatus === 'COMPLETED';
              const isCurrent = activeStep === index;
              return (
                <Step key={step.key} completed={isCompleted}>
                  <StepLabel
                    StepIconProps={{
                      sx: {
                        color: isCurrent
                          ? getStatusColor(step.key)
                          : isCompleted
                          ? '#059669'
                          : '#cbd5e1',
                      },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: isCurrent ? 800 : 600,
                        color: isCurrent ? getStatusColor(step.key) : '#475569',
                      }}
                    >
                      {step.label}
                    </Typography>
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>

          {/* Current Status Banner */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              mt: 2.5,
              pt: 2,
              borderTop: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Current Status:
            </Typography>
            <Chip
              label={currentStatus}
              sx={{
                fontWeight: 800,
                bgcolor: alpha(getStatusColor(currentStatus), 0.12),
                color: getStatusColor(currentStatus),
                border: `1px solid ${alpha(getStatusColor(currentStatus), 0.3)}`,
              }}
            />
          </Stack>
        </Box>

        {/* Quick Transition Action Cards */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5 }}>
            Quick State Transitions
          </Typography>

          <Grid container spacing={2}>
            {currentStatus === 'PENDING_FOR_APPROVAL' && (
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ArrowForwardOutlinedIcon />}
                  onClick={() => handleUpdateStatus('VERIFY')}
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    borderRadius: '14px',
                    textTransform: 'none',
                    fontWeight: 700,
                    borderColor: '#2563eb',
                    color: '#2563eb',
                    bgcolor: alpha('#2563eb', 0.05),
                    '&:hover': { bgcolor: alpha('#2563eb', 0.12) },
                  }}
                >
                  Advance to VERIFY (Check Slots/Vendor)
                </Button>
              </Grid>
            )}

            {currentStatus === 'VERIFY' && (
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ArrowForwardOutlinedIcon />}
                  onClick={() => handleUpdateStatus('NEED_TO_PAYMENT')}
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    borderRadius: '14px',
                    textTransform: 'none',
                    fontWeight: 700,
                    borderColor: '#7c3aed',
                    color: '#7c3aed',
                    bgcolor: alpha('#7c3aed', 0.05),
                    '&:hover': { bgcolor: alpha('#7c3aed', 0.12) },
                  }}
                >
                  Approve for Payment (NEED_TO_PAYMENT)
                </Button>
              </Grid>
            )}

            {(currentStatus === 'VERIFY' || currentStatus === 'NEED_TO_PAYMENT') && (
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<PaymentOutlinedIcon />}
                  onClick={() => handleUpdateStatus('CONFIRM')}
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    borderRadius: '14px',
                    textTransform: 'none',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                    boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)',
                  }}
                >
                  Direct Confirm & Pay (CONFIRM)
                </Button>
              </Grid>
            )}

            {currentStatus === 'CONFIRM' && (
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<TaskAltOutlinedIcon />}
                  onClick={() => handleUpdateStatus('COMPLETED')}
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    borderRadius: '14px',
                    textTransform: 'none',
                    fontWeight: 700,
                    borderColor: '#0d9488',
                    color: '#0d9488',
                    bgcolor: alpha('#0d9488', 0.05),
                    '&:hover': { bgcolor: alpha('#0d9488', 0.12) },
                  }}
                >
                  Mark as COMPLETED
                </Button>
              </Grid>
            )}

            {currentStatus !== 'CANCELLED' && currentStatus !== 'COMPLETED' && (
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<CancelOutlinedIcon />}
                  onClick={() => handleUpdateStatus('CANCELLED')}
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    borderRadius: '14px',
                    textTransform: 'none',
                    fontWeight: 700,
                    borderColor: '#e11d48',
                    color: '#e11d48',
                    bgcolor: alpha('#e11d48', 0.05),
                    '&:hover': { bgcolor: alpha('#e11d48', 0.12) },
                  }}
                >
                  Cancel Order (CANCELLED)
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>

        <Divider sx={{ my: 2.5 }} />

        {/* Custom Target Status Selector & Remarks */}
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth sx={fieldSx}>
              <InputLabel>Custom Target Status</InputLabel>
              <Select
                value={targetStatus}
                label="Custom Target Status"
                onChange={(e) => setTargetStatus(e.target.value)}
                disabled={loading}
              >
                {ALL_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {targetStatus === 'CONFIRM' && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={fieldSx}>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={paymentMode}
                  label="Payment Method"
                  onChange={(e) => setPaymentMode(e.target.value)}
                  disabled={loading}
                >
                  <MenuItem value="WALLET">SiriPay Wallet Debit</MenuItem>
                  <MenuItem value="GATEWAY">Payment Gateway (Razorpay/UPI)</MenuItem>
                  <MenuItem value="ADMIN_OVERRIDE">Admin Manual Settlement</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              label="Admin Remarks / Audit Notes"
              placeholder="e.g. Vendor slot confirmed for 10 AM, customer notified via SMS..."
              value={adminRemarks}
              onChange={(e) => setAdminRemarks(e.target.value)}
              fullWidth
              multiline
              rows={3}
              sx={fieldSx}
              disabled={loading}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, bgcolor: '#fff', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
            color: '#64748b',
          }}
        >
          Close
        </Button>
        <Button
          onClick={() => handleUpdateStatus(targetStatus)}
          variant="contained"
          disabled={loading || targetStatus === currentStatus}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            px: 3.5,
            background: 'linear-gradient(135deg, #0c3483 0%, #11409B 100%)',
            boxShadow: '0 4px 16px rgba(17, 64, 155, 0.25)',
          }}
        >
          {loading ? (
            <CircularProgress size={22} sx={{ color: '#fff' }} />
          ) : (
            `Apply Status: ${targetStatus}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderStatusModal;
